import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from './components/ErrorBoundary'
import { Layout } from './components/layout/Layout'
import { CloudLayout } from './components/layout/CloudLayout'
import { initPostHog, optIn } from './lib/posthog'
import { getCookieConsent, CookieConsent } from './components/CookieConsent'
import { isCloud, isStandalone } from './config/edition'

const DeveloperPage = lazy(() => import('./pages/DeveloperPage').then((m) => ({ default: m.DeveloperPage })))
const DemoPage = lazy(() => import('./pages/DemoPage'))
const UserVerificationPage = lazy(() => import('./pages/UserVerificationPage'))
const PageBuilderPage = lazy(() => import('./pages/PageBuilderPage'))
const LiveCapturePage = lazy(() => import('./pages/LiveCapturePage').then((m) => ({ default: m.LiveCapturePage })))
const MobileVerificationPage = lazy(() => import('./pages/MobileVerificationPage'))
const AdminLogin = lazy(() => import('./pages/AdminLogin').then((m) => ({ default: m.AdminLogin })))
const VerificationManagement = lazy(() => import('./pages/VerificationManagement').then((m) => ({ default: m.VerificationManagement })))
const DevelopersList = lazy(() => import('./pages/DevelopersList').then((m) => ({ default: m.DevelopersList })))
const DocsPage = lazy(() => import('./pages/DocsPage').then((m) => ({ default: m.DocsPage })))
const DocsGuides = lazy(() => import('./pages/DocsGuides').then((m) => ({ default: m.DocsGuides })))
const DocsSdk = lazy(() => import('./pages/DocsSdk').then((m) => ({ default: m.DocsSdk })))
const DocsFeatures = lazy(() => import('./pages/DocsFeatures').then((m) => ({ default: m.DocsFeatures })))
const DocsReference = lazy(() => import('./pages/DocsReference').then((m) => ({ default: m.DocsReference })))
const ReviewDashboardDocs = lazy(() => import('./pages/ReviewDashboardDocs').then((m) => ({ default: m.ReviewDashboardDocs })))
const MarkdownDocsPage = lazy(() => import('./pages/MarkdownDocsPage').then((m) => ({ default: m.MarkdownDocsPage })))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage })))
const LegalPage = lazy(() => import('./pages/LegalPage').then((m) => ({ default: m.LegalPage })))
const SetupPage = lazy(() => import('./pages/SetupPage').then((m) => ({ default: m.SetupPage })))
const VerifyCredentialPage = lazy(() => import('./pages/VerifyCredentialPage').then((m) => ({ default: m.VerifyCredentialPage })))

// Cloud-edition marketing pages (lazy loaded)
const MarketingHome = lazy(() => import('./pages/MarketingHome').then((m) => ({ default: m.MarketingHome })))
const PricingPage = lazy(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })))
const TrustPage = lazy(() => import('./pages/TrustPage').then((m) => ({ default: m.TrustPage })))
const ContactPage = lazy(() => import('./pages/ContactPage').then((m) => ({ default: m.ContactPage })))
const EnterprisePage = lazy(() => import('./pages/EnterprisePage').then((m) => ({ default: m.EnterprisePage })))

function RouteFallback() {
  return (
    <div style={{ minHeight: '50vh', display: 'grid', placeItems: 'center', color: '#64748b', fontSize: 14 }}>
      Loading...
    </div>
  )
}

function App() {
  useEffect(() => {
    initPostHog()
    // Restore consent state for returning users
    if (getCookieConsent() === 'accepted') {
      optIn()
    }
  }, [])

  const Wrap = isCloud ? CloudLayout : Layout

  return (
    <ErrorBoundary>
      <Wrap>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* ── Marketing homepage (cloud edition only) ── */}
            {isCloud && (
              <>
                <Route path="/" element={<MarketingHome />} />
                <Route path="/pricing" element={<PricingPage />} />
                <Route path="/trust" element={<TrustPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/enterprise" element={<EnterprisePage />} />
              </>
            )}

            {/* ── Standalone: Dev Portal at / ── */}
            {isStandalone && (
              <Route path="/" element={<DeveloperPage />} />
            )}

            {/* Dev Portal (standalone only) */}
            {isStandalone && <Route path="/developer" element={<DeveloperPage />} />}

            {/* Demo */}
            <Route path="/demo" element={<DemoPage />} />
            <Route path="/verify" element={<Navigate to="/demo" replace />} />
            <Route path="/verify-credential" element={<VerifyCredentialPage />} />

            {/* Shared routes (standalone only for developer-facing) */}
            <Route path="/user-verification" element={<UserVerificationPage />} />
            <Route path="/v/:slug" element={<UserVerificationPage />} />
            {isStandalone && <Route path="/developer/page-builder" element={<PageBuilderPage />} />}
            <Route path="/live-capture" element={<LiveCapturePage />} />
            <Route path="/verify/mobile" element={<MobileVerificationPage />} />
            <Route path="/docs/markdown" element={<MarkdownDocsPage />} />
            <Route path="/docs/review" element={<ReviewDashboardDocs />} />
            <Route path="/docs/guides" element={<DocsGuides />} />
            <Route path="/docs/sdk" element={<DocsSdk />} />
            <Route path="/docs/features" element={<DocsFeatures />} />
            <Route path="/docs/reference" element={<DocsReference />} />
            <Route path="/docs" element={<DocsPage />} />
            {isStandalone && <Route path="/setup" element={<SetupPage />} />}
            <Route path="/legal" element={<LegalPage />} />
            {isStandalone && <Route path="/admin/login" element={<AdminLogin />} />}
            {isStandalone && <Route path="/admin/verifications" element={<VerificationManagement />} />}
            {isStandalone && <Route path="/admin/developers" element={<DevelopersList />} />}
            {isStandalone && <Route path="/admin/*" element={<Navigate to="/admin/verifications" replace />} />}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </Wrap>
      <CookieConsent />
    </ErrorBoundary>
  )
}

export default App
